import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const category = categories.find((c) => c.slug === article?.category);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16">
          <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300 mb-4">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91n.con/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://www.91n.con/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.91n.con/article/${article.slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishedAt,
        articleBody: article.content.replace(/<[^>]*>/g, ''),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />
      <ScrollToTop />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: category?.name || '分类', href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />
      </div>

      <main className="py-8 md:py-12 bg-background-50">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line"></i>
                  {article.tags.join(' / ')}
                </span>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回 {category?.name || '分类'}
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-400">分享:</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 mt-12">
            <div className="max-w-3xl mx-auto">
              <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-5">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="block bg-background-100 hover:bg-background-200/50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer group"
                  >
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {rel.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                      {rel.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{rel.publishedAt}</span>
                      <span>{rel.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}